/**
 * Tiny i18n (R4.7 framework, R9 Urdu + RTL).
 *
 * A deliberately tiny equivalent of react-i18next's API (~1 KB instead of
 * ~20 KB gzip): every UI string goes through t().
 *
 *   import { t } from 'i18n';                 // anywhere
 *   const { t } = useTranslation();           // in components (same API as react-i18next)
 *   t('storeTheme.title')                     // dotted key into src/locales/<lang>.json
 *   t('storeTheme.contrast', { ratio: '4.6' })  // {{ratio}} interpolation
 *
 * A missing key falls back to English, then to the key itself, so a typo is
 * visible in the UI instead of crashing.
 *
 * R9: the visitor's choice (EN / اردو) is remembered in localStorage
 * ("zaynic-lang"). App.js re-renders (remounts its page tree) on a change, so a
 * plain t() call anywhere picks up the new language. Admin pages always render
 * English (App.js passes "en" there). Urdu sets <html lang="ur" dir="rtl"> and
 * loads Noto Nastaliq Urdu from Google Fonts only then. The Urdu catalog is its
 * own lazy chunk (main bundle budget): index.js waits for it before the first
 * render when Urdu is remembered, and the toggle loads it before switching.
 */
import en from 'locales/en.json';
import { brandVars } from 'config/brand';

const CATALOGS = { en };
const LOADERS = {
  ur: () => import(/* webpackChunkName: "locale-ur" */ 'locales/ur.json'),
};
const FALLBACK = 'en';
export const LANGUAGES = ['en', 'ur'];
export const LANGUAGE_STORAGE_KEY = 'zaynic-lang';
const URDU_FONT_ID = 'zaynic-urdu-font';
const URDU_FONT_HREF = 'https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;500;600;700&display=swap';

function storedLanguage() {
  try {
    const value = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return LANGUAGES.includes(value) ? value : FALLBACK;
  } catch (e) {
    return FALLBACK;
  }
}

/** Loads a language's catalog (no-op when already loaded). Resolves to true when usable. */
export async function loadLanguage(lang) {
  if (CATALOGS[lang]) return true;
  if (!LOADERS[lang]) return false;
  try {
    const mod = await LOADERS[lang]();
    CATALOGS[lang] = mod.default || mod;
    return true;
  } catch (e) {
    return false;
  }
}

let preferred = typeof window === 'undefined' ? FALLBACK : storedLanguage();
let language = preferred;
const listeners = new Set();

function lookup(catalog, key) {
  return key.split('.').reduce((node, part) => (node && typeof node === 'object' ? node[part] : undefined), catalog);
}

export function t(key, vars) {
  let value = lookup(CATALOGS[language], key);
  let from = language;
  if (typeof value !== 'string') {
    value = lookup(CATALOGS[FALLBACK], key);
    from = FALLBACK; // an English fallback string gets the English brand name
  }
  if (typeof value !== 'string') return key;
  // Rebrand: {{brandName}} / {{brandCoins}} / {{brandAssistant}} come from config/brand.js.
  const all = vars ? { ...brandVars(from), ...vars } : brandVars(from);
  return value.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => (all[name] === undefined ? match : String(all[name])));
}

/** True when a key exists (in English) -- for optional per-value labels. */
export function hasKey(key) {
  return typeof lookup(CATALOGS[FALLBACK], key) === 'string';
}

/**
 * Label for a backend enum value (order status, payment method...): the catalog's
 * `<ns>.<value>` when it exists, else the fallback (e.g. the server's English label).
 */
export function tEnum(ns, value, fallback) {
  if (value === undefined || value === null || value === '') return fallback ?? '';
  const key = `${ns}.${value}`;
  return hasKey(key) ? t(key) : (fallback ?? String(value));
}

/**
 * A message the server sent (English): shown as-is in English, so the live English
 * site keeps its exact wording; in Urdu the translated `key` is shown instead.
 */
export function serverText(message, key, vars) {
  return language === FALLBACK && message ? message : t(key, vars);
}

/** The language t() renders right now (English on admin pages). */
export function getLanguage() {
  return language;
}

/** The visitor's remembered choice (may differ from getLanguage() on admin pages). */
export function getPreferredLanguage() {
  return preferred;
}

export function isRtl(lang = language) {
  return lang === 'ur';
}

/** Low-level: which catalog t() reads. App.js calls this during render. */
export function setLanguage(lang) {
  if (CATALOGS[lang]) language = lang;
  return language;
}

function ensureUrduFont() {
  if (typeof document === 'undefined' || document.getElementById(URDU_FONT_ID)) return;
  const link = document.createElement('link');
  link.id = URDU_FONT_ID;
  link.rel = 'stylesheet';
  link.href = URDU_FONT_HREF;
  document.head.appendChild(link);
}

/** Sets <html lang/dir>; loads the Nastaliq font only for Urdu. */
export function applyDocumentLanguage(lang) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('lang', lang);
  root.setAttribute('dir', isRtl(lang) ? 'rtl' : 'ltr');
  if (lang === 'ur') ensureUrduFont();
}

/** The EN / اردو toggle: load the catalog, remember the choice, tell App.js to re-render. */
export async function chooseLanguage(lang) {
  if (!LANGUAGES.includes(lang) || lang === preferred) return preferred;
  if (!(await loadLanguage(lang))) return preferred;
  preferred = lang;
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch (e) {
    /* private mode: the choice lasts for this page only */
  }
  listeners.forEach((fn) => fn(lang));
  return preferred;
}

/**
 * Subscribe to the visitor's language choice in this tab. Other open tabs pick the
 * choice up on their next page load -- switching them live would remount their page
 * and lose whatever the visitor was typing (e.g. a checkout address).
 */
export function subscribeLanguage(fn) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Before the first render: load the remembered language, or fall back to English if it can't load. */
export async function loadPreferredLanguage() {
  if (await loadLanguage(preferred)) return preferred;
  preferred = FALLBACK;
  return preferred;
}

export function useTranslation() {
  return { t, i18n: { language, changeLanguage: chooseLanguage } };
}
